import { useMutation, useSubscription } from '@apollo/client';
import noop from 'lodash/noop';
import React, { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { match } from 'ts-pattern';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Button,
  Card,
  CardContent,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  Icon,
  Progress,
  Select,
  SelectContent,
  SelectTrigger,
  Separator,
  Stack,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@eluve/components';
import {
  allCompletedMedicalNotesFragment,
  listenAppointmentStatusSubscription,
  updateSoapNote,
  useAllSummaries,
  useAppointmentActivity,
  useIsAppointmentSigned,
  useSummary,
} from '@eluve/frontend-appointment-hooks';
import { PromptTemplateSelectItem } from '@eluve/frontend-feature-user-settings';
import { usePromptTemplates } from '@eluve/frontend-feature-user-settings';
import { graphql } from '@eluve/graphql.tada';
import {
  DynamicArtifactRenderer,
  SummaryFeedbackModal,
  SummarySectionMarkdown,
} from '@eluve/smart-blocks';
import { AppointmentSummary, AppointmentSummaryKeys } from '@eluve/utils';

import { useListenForAnySummaries } from './useListenForAnySummaries';

const listenAllSoapNotes = graphql(
  `
    subscription listenAllSoapNotes($tenantId: uuid!, $appointmentId: uuid!) {
      appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
        ...AllCompletedMedicalNotes
      }
    }
  `,
  [allCompletedMedicalNotesFragment],
);

type TemplateDetails = { promptTemplateId: string; outputTemplateId?: string };
type PromptsCarouselProps = {
  selectedLlmTemplates: TemplateDetails[];
  setSelectedLlmTemplates: (
    value: React.SetStateAction<TemplateDetails[]>,
  ) => void;
};

const PromptsCarousel: React.FC<PromptsCarouselProps> = ({
  selectedLlmTemplates,
  setSelectedLlmTemplates,
}) => {
  const {
    activeOutputTemplates: outputTemplates,
    activeSOAPPromptTemplates: promptTemplates,
    defaultOutputTemplateId,
    userSettingsPromptTemplateId,
  } = usePromptTemplates();
  return (
    <Carousel
      opts={{
        align: 'start',
      }}
      className="mx-8 w-[calc(100%-4rem)]"
    >
      <CarouselContent>
        {promptTemplates.map((promptTemplate) => {
          let selectedOutputTemplateId: string | undefined = undefined;
          if (promptTemplate.outputType === 'DYNAMIC_OUTPUT') {
            selectedOutputTemplateId =
              selectedLlmTemplates.find(
                (t) => t.promptTemplateId === promptTemplate.id,
              )?.outputTemplateId ??
              defaultOutputTemplateId ??
              outputTemplates[0]?.id;
          }
          return (
            <CarouselItem
              key={promptTemplate.id}
              className="md:basis-1/2 lg:basis-1/4"
            >
              <Card className="h-full">
                <CardContent className="flex h-full flex-col">
                  <Stack
                    className="border-b p-2"
                    direction="horizontal"
                    align="start"
                  >
                    {userSettingsPromptTemplateId === promptTemplate.id && (
                      <Tooltip>
                        <TooltipTrigger className="hover:cursor-default">
                          <Icon
                            name="Star"
                            size="xl"
                            className="h-5 text-border-accent-quaternary"
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          This is set as your default template. You can change
                          it in your settings.
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <p>{promptTemplate.name}</p>
                  </Stack>
                  <p className="overflow-auto px-2 text-xs font-medium text-text-primary">
                    <Stack gap={2}>
                      {promptTemplate.description ?? ''}
                      {promptTemplate.outputType === 'DYNAMIC_OUTPUT' && (
                        <Select
                          value={selectedOutputTemplateId ?? ''}
                          onValueChange={(value) => {
                            setSelectedLlmTemplates((prev) => {
                              const newValues = prev.filter(
                                (d) => d.promptTemplateId !== promptTemplate.id,
                              );
                              const newTemplate = {
                                promptTemplateId: promptTemplate.id!,
                                outputTemplateId: value,
                              };
                              return [...newValues, newTemplate];
                            });
                          }}
                        >
                          <SelectTrigger>
                            {(() => {
                              const templateOutputId =
                                selectedLlmTemplates.find(
                                  (t) =>
                                    t.promptTemplateId === promptTemplate.id,
                                )?.outputTemplateId ??
                                defaultOutputTemplateId ??
                                outputTemplates[0]?.id;
                              return (
                                outputTemplates.find(
                                  (template) =>
                                    template.id === templateOutputId,
                                )?.name ?? ''
                              );
                            })()}
                          </SelectTrigger>
                          <SelectContent>
                            {outputTemplates.map((template) => (
                              <PromptTemplateSelectItem
                                key={template.id}
                                templateId={template.id}
                                templateName={template.name}
                                templateDescription={null}
                              />
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </Stack>
                  </p>
                  <Button
                    variant="secondary"
                    className="mt-auto rounded-none"
                    onClick={() => {
                      setSelectedLlmTemplates((prev) => {
                        if (
                          selectedLlmTemplates.find(
                            (selectedLlmTemplate) =>
                              selectedLlmTemplate.promptTemplateId ===
                              promptTemplate.id,
                          )
                        ) {
                          return prev.filter(
                            (id) => id.promptTemplateId !== promptTemplate.id,
                          );
                        }
                        return [
                          ...prev,
                          {
                            promptTemplateId: promptTemplate.id!,
                            outputTemplateId: selectedOutputTemplateId,
                          },
                        ];
                      });
                    }}
                  >
                    {selectedLlmTemplates.find(
                      (selectedLlmTemplate) =>
                        selectedLlmTemplate.promptTemplateId ===
                        promptTemplate.id,
                    ) ? (
                      <Icon name="Check" size="xl" className="h-5 text-white" />
                    ) : (
                      'Select'
                    )}
                  </Button>
                </CardContent>
              </Card>
            </CarouselItem>
          );
        })}
      </CarouselContent>
      <CarouselPrevious />
      <CarouselNext />
    </Carousel>
  );
};

export const SummaryComparison: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedSummaryId = searchParams.get('summaryId') ?? undefined;
  const [progress, setProgress] = useState(0);
  const {
    activeOutputTemplates: outputTemplates,
    activeSOAPPromptTemplates: promptTemplates,
  } = usePromptTemplates();

  const { tenantId, appointmentId } = useParams() as {
    tenantId: string;
    appointmentId: string;
  };

  const isNoteSigned = useIsAppointmentSigned();

  const [selectedLlmTemplates, setSelectedLlmTemplates] = useState<
    TemplateDetails[]
  >([]);

  const apiClient = useApiClient();

  const { isAnySummarizationInProgress } = useAppointmentActivity();

  const currentSummary = useSummary();

  const [updateSummary] = useMutation(updateSoapNote, {
    optimisticResponse: (data) => {
      return {
        updateHumanOutputsByPk: {
          __typename: 'HumanOutputs' as const,
          id: data.humanOutputId,
          content: data.content,
          editedFromLlmOutputId: data.llmOutputId,
        },
      };
    },
  });

  const generateSummaries = async () => {
    setProgress(0);
    const internvalId = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(internvalId);
        }
        return prev + 1;
      });
    }, 750);

    if (!selectedLlmTemplates.length) {
      return;
    }

    await apiClient.llm.summarizeAppointmentWithMultipleTemplates({
      body: {
        summarizationDetails: selectedLlmTemplates,
      },
      params: {
        tenantId,
        appointmentId,
      },
    });

    setSelectedLlmTemplates([]);
  };

  const showFeedback = true;

  useListenForAnySummaries();

  useSubscription(listenAppointmentStatusSubscription, {
    variables: {
      tenantId,
      appointmentId,
    },
  });

  useSubscription(listenAllSoapNotes, {
    variables: {
      tenantId,
      appointmentId,
    },
  });

  const { summaries } = useAllSummaries();

  const filteredSummaries = (summaries ?? [])
    .filter((s) => s.isSummaryAvailable)
    .sort((a, b) => {
      if (!a.summarizationCompletedAt || !b.summarizationCompletedAt) {
        // if value is undefined, push to end of list
        return -1;
      }
      return (
        new Date(a.summarizationCompletedAt).valueOf() -
        new Date(b.summarizationCompletedAt).valueOf()
      );
    });

  return (
    <Stack gap={6} className="container my-6 overflow-y-auto">
      <Link to={`/organizations/${tenantId}/appointments/${appointmentId}`}>
        <Button variant="outline">
          Return to your session{' '}
          <Icon name="LogOut" size="xl" className="ml-2" />
        </Button>
      </Link>
      <Stack gap={6}>
        <PromptsCarousel
          selectedLlmTemplates={selectedLlmTemplates}
          setSelectedLlmTemplates={setSelectedLlmTemplates}
        />
        <Stack justify="center" gap={2} direction="horizontal">
          <Button
            disabled={
              !selectedLlmTemplates.length ||
              Boolean(isAnySummarizationInProgress)
            }
            type="submit"
            variant="secondary"
            className="relative w-52 border-none p-0"
            onClick={generateSummaries}
          >
            <Stack
              className="absolute"
              direction="horizontal"
              fullHeight
              align="center"
              justify="center"
              fullWidth={false}
            >
              <Stack
                className="absolute"
                direction="horizontal"
                fullHeight
                align="center"
                justify="center"
                fullWidth={false}
              >
                <Progress
                  value={progress}
                  className={`absolute top-0 left-0 h-10 w-full rounded-sm ${isAnySummarizationInProgress ? 'opacity-30' : 'opacity-0'}`}
                />
              </Stack>
              <Stack
                className="absolute"
                direction="horizontal"
                fullHeight
                align="center"
                justify="center"
                fullWidth={false}
              >
                {isAnySummarizationInProgress
                  ? 'Generating...'
                  : 'Generate Summaries'}
              </Stack>
            </Stack>
          </Button>
        </Stack>

        <Separator />
        <Tabs
          className="w-full"
          value={selectedSummaryId ?? currentSummary.llmOutputId ?? undefined}
        >
          <TabsList className="flex h-auto w-full flex-wrap gap-2 bg-transparent">
            {filteredSummaries.map((llmSummary, index) => {
              const promptTemplateId = llmSummary.promptTemplate.id;
              const promptTemplate = promptTemplates?.find(
                (p) => p.id === promptTemplateId,
              );
              const promptTemplateName =
                promptTemplate?.name ?? `Inactive Template`;
              const outputTemplateId =
                llmSummary.summary.type === 'DYNAMIC'
                  ? llmSummary.outputTemplate?.id
                  : undefined;
              const outputTemplateName = outputTemplates?.find(
                (t) => t.id === outputTemplateId,
              )?.name;
              return (
                <TabsTrigger
                  key={llmSummary.summaryId}
                  value={llmSummary.summaryId}
                  onClick={() => {
                    const urlSearchParams = new URLSearchParams();
                    urlSearchParams.set('summaryId', llmSummary.summaryId);
                    setSearchParams(urlSearchParams, {
                      replace: true,
                    });
                  }}
                  className="flex border bg-border-primary data-[state=active]:shadow-md"
                >
                  {`${promptTemplateName} ${outputTemplateName ? '(' + outputTemplateName + ')' : ''} (#${index + 1})`}
                  {llmSummary.summaryId === currentSummary.llmOutputId && (
                    <Icon
                      name="Check"
                      size="xl"
                      className="h-5 text-border-accent-quaternary"
                    />
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>
          {filteredSummaries.map((llmSummary) => {
            const canSetNewSummary =
              !isNoteSigned &&
              llmSummary.summaryId !== currentSummary.llmOutputId;

            return (
              <TabsContent
                value={llmSummary.summaryId}
                key={llmSummary.summaryId}
              >
                <Stack className="m-4" align="center">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="submit"
                        disabled={!canSetNewSummary}
                        variant="secondary"
                        onClick={async () => {
                          await updateSummary({
                            variables: {
                              content:
                                llmSummary.rawContent as AppointmentSummary,
                              llmOutputId: llmSummary.summaryId,
                              tenantId,
                              humanOutputId: currentSummary!.humanOutputId!,
                            },
                          });
                        }}
                      >
                        Use this summary
                      </Button>
                    </TooltipTrigger>
                    {!canSetNewSummary && (
                      <TooltipContent>
                        {isNoteSigned && 'This note is already signed'}
                        {!isNoteSigned &&
                          llmSummary.summaryId === currentSummary.llmOutputId &&
                          'This note is already set as current summary'}
                      </TooltipContent>
                    )}
                  </Tooltip>

                  <Link
                    to={`/admin/prompts/${llmSummary.promptTemplate.id}/prompt/variant/${llmSummary.promptTemplate.variantNumber}`}
                    className="my-2"
                  >
                    Prompt Template: {llmSummary.promptTemplate.id} / Variant
                    Number: {llmSummary.promptTemplate.variantNumber}
                  </Link>
                  {llmSummary.outputTemplate && (
                    <Link
                      to={`/admin/output-templates/${llmSummary.outputTemplate.id}/variant/${llmSummary.outputTemplate.variantNumber}`}
                      className="my-2"
                    >
                      Output Template: {llmSummary.outputTemplate.id} / Variant
                      Number: {llmSummary.outputTemplate.variantNumber}
                    </Link>
                  )}
                </Stack>
                <Stack gap={2}>
                  {match(llmSummary.summary)
                    .with({ type: 'SOAP' }, (soapSummary) =>
                      soapSummary.data
                        ? Object.entries(soapSummary.data).map(
                            ([summaryKey, summaryValue]) => {
                              return (
                                <SummarySectionMarkdown
                                  key={summaryKey}
                                  summaryKey={
                                    summaryKey as AppointmentSummaryKeys
                                  }
                                  sectionTitle={summaryKey}
                                  content={summaryValue ?? ''}
                                  disabled={true}
                                  handleContentChange={noop}
                                />
                              );
                            },
                          )
                        : null,
                    )
                    .otherwise((dynamicSummary) => (
                      <DynamicArtifactRenderer
                        isReadonly={true}
                        blocks={dynamicSummary.blocks}
                      />
                    ))}

                  {showFeedback && (
                    <Stack justify="end" gap={2} direction="horizontal">
                      <SummaryFeedbackModal
                        appointmentId={appointmentId}
                        llmOutputId={llmSummary.summaryId}
                      />
                    </Stack>
                  )}
                </Stack>
              </TabsContent>
            );
          })}
        </Tabs>
      </Stack>
    </Stack>
  );
};
